import { DealerLayout } from "@/components/shared/DealerLayout";
import { PDFFormFiller } from "@/components/shared/PDFFormFiller";
import { uploadPreApprovedDocument } from "@/lib/api";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;
const PDF_URL = `${API}/api/templates/door_application`;

// Precise pymupdf positions with +8pt Y shift (fields were ~8pt too low)
// PDF page size: 612 x 792. Coords: [x1, y1, x2, y2] bottom-left origin
// Convert pymupdf→PDF: pdf_y = 792 - pymupdf_y
// All fields shifted UP by adding 8 to y1 and y2

const FIELDS = [
  // === PAGE 1 (0-indexed 0) - Cover Page ===
  // "Master Agent:" at [54,286,116,299] → field starts x=120
  { id: "master_agent", page: 0, type: "text", rect: [120, 501, 400, 514], label: "Master Agent", defaultValue: "Prime Media and Marketing LLC" },
  // "Date:" at [411,286,437,299] → field starts x=440
  { id: "date_p0", page: 0, type: "text", rect: [440, 501, 560, 514], label: "Date" },
  // "Business Name:" at [53,321,125,334] → field starts x=130
  { id: "business_name_p0", page: 0, type: "text", rect: [130, 466, 360, 479], label: "Business Name" },
  // "Business Address:" at [53,349,132,362] → field starts x=135
  { id: "business_addr_p0", page: 0, type: "text", rect: [135, 438, 360, 451], label: "Business Address" },
  // "Existing ID:" at [368,324,479,337] → field starts x=480
  { id: "existing_id", page: 0, type: "text", rect: [480, 463, 560, 476], label: "Existing ID" },
  // "City/State/Zip:" at [368,349,427,362] → field starts x=430
  { id: "city_state_zip_p0", page: 0, type: "text", rect: [430, 438, 560, 451], label: "City/State/Zip" },

  // === PAGE 2 (0-indexed 1) - Dealer Profile Sheet ===
  // "Business Name:" at [54,102,126,115] → field at x=130
  { id: "business_name", page: 1, type: "text", rect: [130, 685, 560, 698], label: "Business Name" },
  // Issue 3: Address fields aligned with PDF lines
  // Street Address line at pymupdf y=150.8 → PDF y=641
  // Line x=121.5-468.0 (street), x=468.5-558.0 (unit)
  { id: "street_address", page: 1, type: "text", rect: [122, 641, 468, 654], label: "Street Address" },
  { id: "unit", page: 1, type: "text", rect: [469, 641, 558, 654], label: "Unit #" },
  // City/State/ZIP line at pymupdf y=187.5 → PDF y=605
  { id: "city", page: 1, type: "text", rect: [122, 605, 398, 618], label: "City" },
  { id: "state_p1", page: 1, type: "text", rect: [399, 605, 468, 618], label: "State" },
  { id: "zip", page: 1, type: "text", rect: [469, 605, 558, 618], label: "ZIP Code" },
  // "Business Phone:" at [54,216,128,229]
  { id: "business_phone", page: 1, type: "text", rect: [130, 571, 305, 584], label: "Business Phone" },
  // "Business Email" at [315,216,383,229]
  { id: "business_email", page: 1, type: "text", rect: [385, 571, 560, 584], label: "Business Email" },
  // "FEIN/Tax ID:" at [54,242,112,255]
  { id: "fein", page: 1, type: "text", rect: [115, 545, 305, 558], label: "FEIN/Tax ID" },
  // "State Resale Certificate #:" at [54,275,167,288]
  { id: "resale_cert", page: 1, type: "text", rect: [170, 512, 435, 525], label: "Resale Cert #" },
  // "State:" at [441,275,468,288]
  { id: "resale_state", page: 1, type: "text", rect: [470, 512, 560, 525], label: "State" },
  // "Improvement completion:" at [54,319,163,332]
  { id: "tenant_date", page: 1, type: "text", rect: [170, 468, 560, 481], label: "Expected Date" },
  // "Total Square Footage:" at [54,352,151,365]
  { id: "total_sqft", page: 1, type: "text", rect: [155, 435, 560, 448], label: "Total SQFT" },
  // "Overall Dimensions:" at [54,386,142,398]
  { id: "dimensions", page: 1, type: "text", rect: [145, 402, 560, 415], label: "Dimensions" },
  // "Signage Footage:" at [54,420,132,433]
  { id: "signage_sqft", page: 1, type: "text", rect: [135, 367, 560, 380], label: "Signage SQFT" },

  // Issue 6: Type of Location checkboxes - exact pymupdf positions
  // pymupdf rects: x=154-163 y=458-466 → PDF [154, 326, 163, 334]
  { id: "cb_ma_owned", page: 1, type: "checkbox", rect: [154, 326, 163, 334], label: "MA Owned" },
  { id: "cb_new_loc", page: 1, type: "checkbox", rect: [271, 326, 280, 334], label: "New Location" },
  { id: "cb_multi", page: 1, type: "checkbox", rect: [357, 326, 366, 334], label: "Multi-Carrier" },
  { id: "cb_owner_change", page: 1, type: "checkbox", rect: [456, 326, 465, 334], label: "Ownership Change" },

  // "Exclusive to Brand:" at [164,475,253,488]
  { id: "exclusive_brand", page: 1, type: "text", rect: [255, 312, 560, 325], label: "Exclusive to Brand" },

  // Issue 7: Store Hours - lowered slightly (y decreased by 7 to counter +4 global shift)
  { id: "hours_mon", page: 1, type: "text", rect: [180, 246, 340, 259], label: "Monday" },
  { id: "hours_tue", page: 1, type: "text", rect: [180, 235, 340, 247], label: "Tuesday" },
  { id: "hours_wed", page: 1, type: "text", rect: [180, 223, 340, 236], label: "Wednesday" },
  { id: "hours_thu", page: 1, type: "text", rect: [180, 212, 340, 224], label: "Thursday" },
  { id: "hours_fri", page: 1, type: "text", rect: [180, 200, 340, 213], label: "Friday" },
  { id: "hours_sat", page: 1, type: "text", rect: [180, 189, 340, 201], label: "Saturday" },
  { id: "hours_sun", page: 1, type: "text", rect: [180, 178, 340, 190], label: "Sunday" },

  // Issue 20: Kit checkboxes
  // "Kit 1: 8 FT Flag" checkbox at [407,543,448,556]
  { id: "cb_kit1", page: 1, type: "checkbox", rect: [390, 244, 403, 257], label: "8 FT Flag" },
  // "Kit 2: 15 FT Flag" checkbox at [407,565,453,578]
  { id: "cb_kit2", page: 1, type: "checkbox", rect: [390, 222, 403, 235], label: "15 FT Flag" },

  // === PAGE 3 (0-indexed 2) - Contact Info ===
  // "Principal/Owner Name:" at [54,92,154,105]
  { id: "principal_name", page: 2, type: "text", rect: [158, 695, 560, 708], label: "Principal/Owner Name" },
  // "Title:" at [54,125,77,138]
  { id: "principal_title", page: 2, type: "text", rect: [80, 662, 560, 675], label: "Title" },
  // Issue 4: Residential Address aligned with PDF lines
  // Street line at pymupdf y=162.8 → PDF y=629, x=139.5-468.0 / 468.5-558.0
  { id: "principal_street", page: 2, type: "text", rect: [140, 629, 468, 642], label: "Street Address" },
  { id: "principal_apt", page: 2, type: "text", rect: [469, 629, 558, 642], label: "Apt/Unit" },
  // City/State/ZIP line at pymupdf y=199.6 → PDF y=592, x=121.5-398.3 / 398.8-468.0 / 468.5-558.0
  { id: "principal_city", page: 2, type: "text", rect: [122, 592, 398, 605], label: "City" },
  { id: "principal_state", page: 2, type: "text", rect: [399, 592, 468, 605], label: "State" },
  { id: "principal_zip", page: 2, type: "text", rect: [469, 592, 558, 605], label: "ZIP" },
  // "Mobile Phone:" at [54,228,117,241]
  { id: "principal_phone", page: 2, type: "text", rect: [120, 559, 305, 572], label: "Mobile Phone" },
  // "Email:" at [314,228,346,241]
  { id: "principal_email", page: 2, type: "text", rect: [350, 559, 560, 572], label: "Email" },

  // Partner Info
  // "Partner Name:" at [54,295,118,307]
  { id: "partner_name", page: 2, type: "text", rect: [120, 493, 560, 505], label: "Partner Name" },
  // Issue 5: Partner Address aligned with PDF lines
  // Street line at pymupdf y=350.6 → PDF y=441, x=121.5-468.0 / 468.5-558.0
  { id: "partner_street", page: 2, type: "text", rect: [122, 441, 468, 454], label: "Street Address" },
  { id: "partner_apt", page: 2, type: "text", rect: [469, 441, 558, 454], label: "Apt/Unit" },
  // City/State/ZIP line at pymupdf y=387.4 → PDF y=405, x=121.5-398.3 / 398.8-468.0 / 468.5-558.0
  { id: "partner_city", page: 2, type: "text", rect: [122, 405, 398, 418], label: "City" },
  { id: "partner_state", page: 2, type: "text", rect: [399, 405, 468, 418], label: "State" },
  { id: "partner_zip", page: 2, type: "text", rect: [469, 405, 558, 418], label: "ZIP" },
  // "Mobile Phone:" at [54,416,117,429]
  { id: "partner_phone", page: 2, type: "text", rect: [120, 371, 305, 384], label: "Mobile Phone" },
  // "Email:" at [314,416,346,429]
  { id: "partner_email", page: 2, type: "text", rect: [350, 371, 560, 384], label: "Email" },

  // Credit Reference 1
  // "Full Name:" at [54,520,103,532]
  { id: "ref1_name", page: 2, type: "text", rect: [105, 268, 390, 280], label: "Full Name" },
  // "Relationship:" at [400,520,457,532]
  { id: "ref1_rel", page: 2, type: "text", rect: [460, 268, 560, 280], label: "Relationship" },
  // "Company:" at [54,538,100,551]
  { id: "ref1_company", page: 2, type: "text", rect: [102, 249, 415, 262], label: "Company" },
  // "Phone:" at [424,538,457,551]
  { id: "ref1_phone", page: 2, type: "text", rect: [460, 249, 560, 262], label: "Phone" },
  // "Address:" at [54,557,94,569]
  { id: "ref1_addr", page: 2, type: "text", rect: [97, 231, 560, 243], label: "Address" },

  // Credit Reference 2
  // "Full Name:" at [54,582,103,595]
  { id: "ref2_name", page: 2, type: "text", rect: [105, 205, 390, 218], label: "Full Name" },
  // "Relationship:" at [400,582,457,595]
  { id: "ref2_rel", page: 2, type: "text", rect: [460, 205, 560, 218], label: "Relationship" },
  // "Company:" at [54,601,100,613]
  { id: "ref2_company", page: 2, type: "text", rect: [102, 187, 415, 199], label: "Company" },
  // "Phone:" at [424,601,457,613]
  { id: "ref2_phone", page: 2, type: "text", rect: [460, 187, 560, 199], label: "Phone" },
  // "Address:" at [54,619,94,632]
  { id: "ref2_addr", page: 2, type: "text", rect: [97, 168, 560, 181], label: "Address" },

  // Credit Reference 3
  // "Full Name:" at [54,645,103,658]
  { id: "ref3_name", page: 2, type: "text", rect: [105, 142, 390, 155], label: "Full Name" },
  // "Relationship:" at [400,645,457,658]
  { id: "ref3_rel", page: 2, type: "text", rect: [460, 142, 560, 155], label: "Relationship" },
  // "Company:" at [54,664,100,676]
  { id: "ref3_company", page: 2, type: "text", rect: [102, 124, 415, 136], label: "Company" },
  // "Phone:" at [424,664,457,676]
  { id: "ref3_phone", page: 2, type: "text", rect: [460, 124, 560, 136], label: "Phone" },
  // "Address:" at [54,682,94,695]
  { id: "ref3_addr", page: 2, type: "text", rect: [97, 105, 560, 118], label: "Address" },

  // === PAGE 4 (0-indexed 3) - Disclaimer & Signature ===
  // Issue 10: Checkbox - exact pymupdf position x=58-69, y=96-108 → PDF [58, 684, 69, 696]
  { id: "cb_disclaimer", page: 3, type: "checkbox", rect: [58, 684, 69, 696], label: "Certify", defaultValue: "on" },
  // Issue 6: Location address - exact underscore width (x=326.2-537.7)
  { id: "disclaimer_addr", page: 3, type: "text", rect: [326, 683, 538, 696], label: "Location Address" },
  // Issue 12: Signature box - half width
  // "Signature:" at pymupdf [54,232,100,245] → PDF y=547-560
  { id: "sig_main", page: 3, type: "signature", rect: [105, 547, 262, 560], label: "Signature" },
  // "Date:" at pymupdf [426,232,449,245] → PDF y=547-560
  { id: "sig_date", page: 3, type: "text", rect: [455, 547, 560, 560], label: "Date" },
  // Issue 11: sig_name REMOVED per user request
  // Issue 13: Title moved up to signature line and left
  // "Title" at pymupdf [306,243,327,256] → move up to signature line y=547-560
  { id: "sig_title", page: 3, type: "text", rect: [275, 547, 420, 560], label: "Title" },
];

const MANUAL_TEXT_MAPPING = FIELDS
  .filter((f) => f.type === "text")
  .map((f) => ({
    fieldId: f.id,
    page: f.page,
    x: f.rect[0] + 2,
    y: f.rect[1] + 3,
    fontSize: 9,
  }));

export default function DoorApplicationGenerator() {
  const navigate = useNavigate();

  return (
    <DealerLayout>
      <PDFFormFiller
        pdfUrl={PDF_URL}
        fields={FIELDS}
        title="Exclusive Door Application"
        subtitle="Total by Verizon New Dealer Application - Fill in the highlighted fields on each page."
        docType="door_application"
        onSave={(file, docType) => uploadPreApprovedDocument(file, docType).then(() => navigate("/dealer/documents"))}
        onBack={() => navigate("/dealer/documents")}
        manualTextMapping={MANUAL_TEXT_MAPPING}
      />
    </DealerLayout>
  );
}
